import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'

interface TireAxleSize {
    axleNumber: number
    side: string
    tireSize: string
    positionLabel?: string
}

interface TireDispatchDetails {
    dispatchId: string
    status: string
    vendorName: string
    vendorLocation?: string
    vendorContactName?: string
    vendorPhone?: string
    problemType: string
    equipment: string
    equipmentDescription: string
    serialNumber: string
    makeModel: string
    usage: string
    job: string
    jobAddress: string
    county: string
    locationDescription?: string
    locationLatitude?: number | null
    locationLongitude?: number | null
    locationMapUrl?: string
    axleNumber?: number
    side: string
    resolvedTireSize: string
    submitterName: string
    submitterPhone: string
    allAxleSizes: TireAxleSize[]
    damageImageUrls: string[]
    alreadyResponded: boolean
    responseEta: string
}

@Component({
    selector: 'app-tire-dispatch',
    templateUrl: './tire-dispatch.component.html',
    standalone: true,
    imports: [CommonModule, FormsModule],
})
export class TireDispatchComponent implements OnInit {
    private http = inject(HttpClient)
    private route = inject(ActivatedRoute)

    private token = ''
    status: 'loading' | 'ready' | 'submitted' | 'error' = 'loading'
    details: TireDispatchDetails | null = null
    responseEta = ''
    notes = ''
    declined = false
    submitStatus: 'idle' | 'submitting' | 'error' = 'idle'
    receiptFiles: File[] = []
    receiptUploadStatus: 'idle' | 'uploading' | 'done' | 'error' = 'idle'

    ngOnInit() {
        this.token = this.route.snapshot.paramMap.get('token') ?? ''
        if (!this.token) {
            this.status = 'error'
            return
        }

        this.http.get<TireDispatchDetails>(`api/TireDispatch/${encodeURIComponent(this.token)}`).subscribe({
            next: details => {
                this.details = details
                this.responseEta = details.responseEta ?? ''
                this.status = details.alreadyResponded ? 'submitted' : 'ready'
            },
            error: () => (this.status = 'error'),
        })
    }

    submitResponse() {
        if (!this.token || this.submitStatus === 'submitting') return
        if (!this.declined && !this.responseEta.trim()) return

        this.submitStatus = 'submitting'
        this.http
            .post('api/TireDispatch/Confirm', {
                token: this.token,
                responseEta: this.responseEta.trim(),
                declined: this.declined,
                notes: this.notes.trim() || null,
            })
            .subscribe({
                next: () => {
                    this.submitStatus = 'idle'
                    this.status = 'submitted'
                },
                error: () => (this.submitStatus = 'error'),
            })
    }

    onReceiptSelected(event: Event) {
        const input = event.target as HTMLInputElement
        if (input.files?.length) {
            this.receiptFiles = Array.from(input.files)
        }
    }

    uploadReceipt() {
        if (!this.token || !this.receiptFiles.length || this.receiptUploadStatus === 'uploading') return

        const form = new FormData()
        for (let i = 0; i < this.receiptFiles.length; i++) {
            form.append('myFile' + i, this.receiptFiles[i], this.receiptFiles[i].name)
        }

        this.receiptUploadStatus = 'uploading'
        this.http.post(`api/TireDispatch/UploadReceipt?token=${encodeURIComponent(this.token)}`, form).subscribe({
            next: () => {
                this.receiptUploadStatus = 'done'
                this.receiptFiles = []
            },
            error: () => (this.receiptUploadStatus = 'error'),
        })
    }
}
